/**
 * Curve math for the profile / path editors, mirroring
 * engine/crates/prism_model/src/curves.rs so the 2D preview matches the mesh
 * prism builds: centripetal Catmull-Rom (`catmull_rom`), the profile
 * shorthands (`profile_points`) and loft section rings (`section_ring`).
 * Plus pure point-list edits (add / insert / delete / snap) used by the
 * editors and their tests.
 */

export type Vec2 = [number, number];
export type Vec3 = [number, number, number];
type Pt = readonly number[];

const MAX_POINTS = 20_000;

const sub = (a: Pt, b: Pt) => a.map((v, i) => v - (b[i] ?? 0));
const add = (a: Pt, b: Pt) => a.map((v, i) => v + (b[i] ?? 0));
const mul = (a: Pt, k: number) => a.map((v) => v * k);
export const dist = (a: Pt, b: Pt) => Math.hypot(...sub(a, b));

/**
 * Centripetal Catmull-Rom through `pts` with `samples` segments per span
 * (prism `catmull_rom`): passes through every point, no overshoot on uneven
 * spacing; `closed` wraps the last point back to the first. Fewer than 3
 * points or `samples <= 1` return the input unchanged. Works in 2D or 3D.
 */
export function catmullRom<T extends Pt>(pts: readonly T[], closed: boolean, samples: number): T[] {
  const n = pts.length;
  const s = Math.min(64, Math.max(1, Math.floor(samples)));
  if (n < 3 || s === 1) return pts.map((p) => p.slice() as unknown as T);
  const get = (i: number): number[] => {
    if (closed) return pts[((i % n) + n) % n]!.slice();
    if (i < 0) return sub(mul(pts[0]!, 2), pts[1]!);
    if (i >= n) return sub(mul(pts[n - 1]!, 2), pts[n - 2]!);
    return pts[i]!.slice();
  };
  const knot = (a: Pt, b: Pt) => Math.max(Math.sqrt(dist(a, b)), 1e-6);
  const lerp2 = (a: Pt, wa: number, b: Pt, wb: number) => add(mul(a, wa), mul(b, wb));
  const spans = closed ? n : n - 1;
  const out: number[][] = [];
  for (let span = 0; span < spans; span++) {
    const [p0, p1, p2, p3] = [get(span - 1), get(span), get(span + 1), get(span + 2)];
    const t1 = knot(p0, p1);
    const t2 = t1 + knot(p1, p2);
    const t3 = t2 + knot(p2, p3);
    for (let k = 0; k < s; k++) {
      const t = t1 + ((t2 - t1) * k) / s;
      const a1 = lerp2(p0, (t1 - t) / t1, p1, t / t1);
      const a2 = lerp2(p1, (t2 - t) / (t2 - t1), p2, (t - t1) / (t2 - t1));
      const a3 = lerp2(p2, (t3 - t) / (t3 - t2), p3, (t - t2) / (t3 - t2));
      const b1 = lerp2(a1, (t2 - t) / t2, a2, t / t2);
      const b2 = lerp2(a2, (t3 - t) / (t3 - t1), a3, (t - t1) / (t3 - t1));
      out.push(lerp2(b1, (t2 - t) / (t2 - t1), b2, (t - t1) / (t2 - t1)));
    }
  }
  if (!closed) out.push(pts[n - 1]!.slice());
  return out.slice(0, MAX_POINTS) as unknown as T[];
}

/** The polyline prism actually builds: smoothed when `smooth` and 3+ points. */
export function previewCurve<T extends Pt>(pts: readonly T[], closed: boolean, smooth: boolean, samples: number): T[] {
  return smooth && pts.length >= 3 ? catmullRom(pts, closed, samples) : pts.map((p) => p.slice() as unknown as T);
}

/**
 * Read-only Bezier handles at every control point, derived from the
 * Catmull-Rom tangents (a cubic with control points p ± tangent/3 per span
 * has the same slope there). `[in, out]` per point.
 */
export function bezierHandles<T extends Pt>(pts: readonly T[], closed: boolean): [number[], number[]][] {
  const n = pts.length;
  if (n < 3) return pts.map((p) => [p.slice(), p.slice()]);
  const s = 16;
  const curve = catmullRom(pts, closed, s);
  return pts.map((p, i) => {
    const at = i * s;
    const next = curve[at + 1];
    const prev = at > 0 ? curve[at - 1] : closed ? curve[curve.length - 1] : undefined;
    // Derivative per span parameter ≈ (neighbouring sample - p) * samples.
    const out = next && !(i === n - 1 && !closed) ? add(p, mul(sub(next, p), s / 3)) : p.slice();
    const inn = prev ? add(p, mul(sub(prev, p), s / 3)) : p.slice();
    return [inn, out];
  });
}

/** Closed 2D profile from the shorthand fields (prism `profile_points`); null when none is set. */
export function profilePoints(
  profile: readonly Pt[] | undefined,
  ellipse: Pt | undefined,
  rect: Pt | undefined,
  radius: number | undefined,
  segmentsIn: number,
  smooth: boolean,
): Vec2[] | null {
  const segments = Math.min(512, Math.max(3, Math.floor(segmentsIn)));
  if (profile && profile.length >= 3) {
    let pts = profile.map((p) => [p[0] ?? 0, p[1] ?? 0] as Vec2);
    const first = pts[0]!;
    const last = pts[pts.length - 1]!;
    if (first[0] === last[0] && first[1] === last[1]) pts = pts.slice(0, -1);
    if (smooth) pts = catmullRom(pts, true, Math.max(2, Math.floor(segments / pts.length)));
    return pts;
  }
  if (rect) {
    const hw = Math.abs(rect[0] ?? 0) * 0.5;
    const hh = Math.abs(rect[1] ?? 0) * 0.5;
    const r = Math.min(Math.max(rect[2] ?? 0, 0), Math.min(hw, hh));
    if (r <= 1e-6) return [[hw, -hh], [hw, hh], [-hw, hh], [-hw, -hh]];
    const per = Math.max(1, Math.floor(segments / 4));
    const corners: [Vec2, number][] = [
      [[hw - r, -hh + r], -Math.PI / 2],
      [[hw - r, hh - r], 0],
      [[-hw + r, hh - r], Math.PI / 2],
      [[-hw + r, -hh + r], Math.PI],
    ];
    const pts: Vec2[] = [];
    for (const [c, start] of corners) {
      for (let k = 0; k <= per; k++) {
        const a = start + (Math.PI / 2) * (k / per);
        pts.push([c[0] + Math.cos(a) * r, c[1] + Math.sin(a) * r]);
      }
    }
    return pts;
  }
  const radii = ellipse ? [ellipse[0] ?? 0, ellipse[1] ?? 0] : radius !== undefined ? [radius, radius] : null;
  if (!radii) return null;
  return Array.from({ length: segments }, (_, k) => {
    const a = (k / segments) * Math.PI * 2;
    return [Math.cos(a) * radii[0]!, Math.sin(a) * radii[1]!] as Vec2;
  });
}

/** Euler XYZ degrees (glam `EulerRot::XYZ`: Rx · Ry · Rz) applied to `p`. */
export function rotateXYZ(p: Pt, deg: Pt): Vec3 {
  const [a, b, c] = [deg[0] ?? 0, deg[1] ?? 0, deg[2] ?? 0].map((d) => (d * Math.PI) / 180) as Vec3;
  let [x, y, z] = [p[0] ?? 0, p[1] ?? 0, p[2] ?? 0];
  // Rz
  [x, y] = [x * Math.cos(c) - y * Math.sin(c), x * Math.sin(c) + y * Math.cos(c)];
  // Ry
  [x, z] = [x * Math.cos(b) + z * Math.sin(b), -x * Math.sin(b) + z * Math.cos(b)];
  // Rx
  [y, z] = [y * Math.cos(a) - z * Math.sin(a), y * Math.sin(a) + z * Math.cos(a)];
  return [x, y, z];
}

/** A loft section's profile placed in 3D (prism `section_ring`): profile `[x, z]` in the section's XZ plane. */
export function sectionRing(profile: readonly Vec2[], position: Pt, rotation: Pt, scale: Pt): Vec3[] {
  return profile.map((p) => {
    const r = rotateXYZ([p[0] * (scale[0] ?? 1), 0, p[1] * (scale[1] ?? 1)], rotation);
    return [r[0] + (position[0] ?? 0), r[1] + (position[1] ?? 0), r[2] + (position[2] ?? 0)];
  });
}

/* ------------------------------------------------------------------ */
/* Point-list edits                                                    */

export const snapValue = (v: number, grid: number) => (grid > 0 ? Math.round(v / grid) * grid : v);

/** Snap every coordinate to the grid (rounded to kill float noise). */
export function snapPoint<T extends Pt>(p: T, grid: number): T {
  return p.map((v) => Math.round(snapValue(v, grid) * 1e6) / 1e6) as unknown as T;
}

export function appendPoint<T>(points: readonly T[], p: T): T[] {
  return [...points, p];
}

/** Insert `p` after index `segment` (the segment from `segment` to `segment + 1`). */
export function insertPoint<T>(points: readonly T[], segment: number, p: T): T[] {
  const next = points.slice();
  next.splice(segment + 1, 0, p);
  return next;
}

export function deletePoint<T>(points: readonly T[], index: number): T[] {
  return points.filter((_, i) => i !== index);
}

export function movePoint<T>(points: readonly T[], index: number, p: T): T[] {
  return points.map((q, i) => (i === index ? p : q));
}

/** Closest point on segment ab to p: `{ t, point, distance }`. */
export function closestOnSegment(p: Pt, a: Pt, b: Pt): { t: number; point: number[]; distance: number } {
  const ab = sub(b, a);
  const len2 = ab.reduce((s, v) => s + v * v, 0);
  const t = len2 > 0 ? Math.min(1, Math.max(0, sub(p, a).reduce((s, v, i) => s + v * ab[i]!, 0) / len2)) : 0;
  const point = add(a, mul(ab, t));
  return { t, point, distance: dist(p, point) };
}

/**
 * Segment of the polyline nearest to `p` (control polygon, wrapping when
 * `closed`), ignoring hits at the very ends of a segment (those are the
 * points themselves). Null when none is within `maxDistance`.
 */
export function nearestSegment(
  points: readonly Pt[],
  p: Pt,
  closed: boolean,
  maxDistance = Infinity,
): { segment: number; t: number; point: number[]; distance: number } | null {
  const n = points.length;
  const count = closed && n >= 3 ? n : n - 1;
  let best: { segment: number; t: number; point: number[]; distance: number } | null = null;
  for (let i = 0; i < count; i++) {
    const hit = closestOnSegment(p, points[i]!, points[(i + 1) % n]!);
    if (hit.t <= 0.02 || hit.t >= 0.98 || hit.distance > maxDistance) continue;
    if (!best || hit.distance < best.distance) best = { segment: i, ...hit };
  }
  return best;
}

/** Structural edits of a point list, so parallel arrays (sweep `scale`, loft sections) follow. */
export type PointEdit =
  /** Appended; `from` = the point selected when it was added (skeleton parent). */
  | { kind: 'add'; index: number; from?: number | null }
  | { kind: 'insert'; index: number }
  | { kind: 'delete'; index: number };

/**
 * Replays point-list edits on a parallel array. `make(index, arr)` creates
 * the entry for a new point (e.g. the mean of its neighbours).
 */
export function applyEdits<T>(items: readonly T[], edits: readonly PointEdit[], make: (index: number, items: readonly T[]) => T): T[] {
  let next = items.slice();
  for (const e of edits) {
    if (e.kind === 'delete') next = next.filter((_, i) => i !== e.index);
    else {
      const at = Math.min(e.index, next.length);
      const copy = next.slice();
      copy.splice(at, 0, make(at, next));
      next = copy;
    }
  }
  return next;
}

/** Bounds of a set of 2D points: `[minX, minY, maxX, maxY]`, or null when empty. */
export function bounds2(points: readonly Pt[]): [number, number, number, number] | null {
  if (!points.length) return null;
  let [x0, y0, x1, y1] = [Infinity, Infinity, -Infinity, -Infinity];
  for (const p of points) {
    x0 = Math.min(x0, p[0] ?? 0);
    y0 = Math.min(y0, p[1] ?? 0);
    x1 = Math.max(x1, p[0] ?? 0);
    y1 = Math.max(y1, p[1] ?? 0);
  }
  return [x0, y0, x1, y1];
}

/* ------------------------------------------------------------------ */
/* Orthographic projections for 3D paths                               */

export type ViewName = 'top' | 'front' | 'side';

/**
 * Screen axes of each view (model front is -Z): top looks down -Y with
 * forward (-Z) up the screen, front looks at the X·Y plane, side looks from
 * +X so forward (-Z) points right.
 */
export const VIEWS: Record<ViewName, { label: string; axes: [string, string] }> = {
  top: { label: 'Top', axes: ['X', '−Z'] },
  front: { label: 'Front', axes: ['X', 'Y'] },
  side: { label: 'Side', axes: ['−Z', 'Y'] },
};

export function project(p: Pt, view: ViewName): Vec2 {
  const [x, y, z] = [p[0] ?? 0, p[1] ?? 0, p[2] ?? 0];
  if (view === 'top') return [x, -z];
  if (view === 'front') return [x, y];
  return [-z, y];
}

/** Inverse of {@link project}: the hidden coordinate comes from `keep`. */
export function unproject(uv: Pt, view: ViewName, keep: Pt): Vec3 {
  const [u, v] = [uv[0] ?? 0, uv[1] ?? 0];
  if (view === 'top') return [u, keep[1] ?? 0, -v];
  if (view === 'front') return [u, v, keep[2] ?? 0];
  return [keep[0] ?? 0, v, -u];
}

/** Round to 4 decimals for storage (keeps documents readable). */
export const tidy = (v: number) => Math.round(v * 1e4) / 1e4 + 0;
export const tidyPoint = <T extends Pt>(p: T): T => p.map(tidy) as unknown as T;
